/**
 * Facebook Pixel Tracking Utilities
 * Professional implementation with performance optimization
 */

// Extend Window interface
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

// Facebook Pixel ID - Replace with your actual Pixel ID
export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID || '';

/**
 * Initialize Facebook Pixel (called automatically by component)
 */
export const initFacebookPixel = () => {
  if (typeof window === 'undefined' || !FB_PIXEL_ID) return;

  // Check if already initialized
  if (window.fbq) return;

  // Initialize fbq function
  window.fbq = function () {
    // eslint-disable-next-line prefer-rest-params
    window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
  };

  if (!window._fbq) window._fbq = window.fbq;
  window.fbq.push = window.fbq;
  window.fbq.loaded = true;
  window.fbq.version = '2.0';
  window.fbq.queue = [];

  // Initialize pixel
  window.fbq('init', FB_PIXEL_ID);

  // Track initial page view
  window.fbq('track', 'PageView');

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Facebook Pixel initialized:', FB_PIXEL_ID);
  }
};

/**
 * Track a standard Facebook event
 * @param eventName - Standard Facebook event name
 * @param params - Event parameters
 */
export const trackFacebookEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.fbq || !FB_PIXEL_ID) return;

  try {
    window.fbq('track', eventName, params);

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Facebook Pixel Event:', eventName, params);
    }
  } catch (error) {
    console.error('Facebook Pixel tracking error:', error);
  }
};

/**
 * Track a custom Facebook event
 * @param eventName - Custom event name
 * @param params - Event parameters
 */
export const trackFacebookCustomEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.fbq || !FB_PIXEL_ID) return;

  try {
    window.fbq('trackCustom', eventName, params);

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Facebook Pixel Custom Event:', eventName, params);
    }
  } catch (error) {
    console.error('Facebook Pixel custom tracking error:', error);
  }
};

/**
 * Track page view (for client-side navigation)
 */
export const trackFacebookPageView = () => {
  trackFacebookEvent('PageView');
};

/**
 * Track product view
 * @param product - Product details
 */
export const trackFacebookViewContent = (product: {
  id?: string;
  title?: string;
  price?: number | string;
  category?: string;
}) => {
  trackFacebookEvent('ViewContent', {
    content_name: product.title || 'Unknown Product',
    content_category: product.category || 'Air Filters',
    content_ids: [product.id],
    content_type: 'product',
    value: parseFloat(String(product.price)) || 0,
    currency: 'USD',
  });
};

/**
 * Track add to cart
 * @param product - Product details
 * @param quantity - Quantity added
 */
export const trackFacebookAddToCart = (
  product: {
    id?: string;
    title?: string;
    price?: number | string;
    category?: string;
  },
  quantity: number = 1
) => {
  trackFacebookEvent('AddToCart', {
    content_name: product.title || 'Unknown Product',
    content_category: product.category || 'Air Filters',
    content_ids: [product.id],
    content_type: 'product',
    value: parseFloat(String(product.price)) * quantity || 0,
    currency: 'USD',
  });
};

/**
 * Track checkout initiated
 * @param cartData - Cart details
 */
export const trackFacebookInitiateCheckout = (cartData: {
  total?: number | string;
  items?: any[];
  itemCount?: number;
}) => {
  const contentIds = cartData.items?.map((item) => item.id || item.item_id) || [];

  trackFacebookEvent('InitiateCheckout', {
    content_ids: contentIds,
    content_type: 'product',
    value: parseFloat(String(cartData.total)) || 0,
    currency: 'USD',
    num_items: cartData.itemCount || contentIds.length,
  });
};

/**
 * Track purchase (conversion)
 * @param orderData - Order details
 */
export const trackFacebookPurchase = (orderData: {
  orderId?: string;
  total?: number | string;
  items?: any[];
}) => {
  const contentIds = orderData.items?.map((item) => item.id || item.item_id) || [];

  trackFacebookEvent('Purchase', {
    content_ids: contentIds,
    content_type: 'product',
    value: parseFloat(String(orderData.total)) || 0,
    currency: 'USD',
    num_items: contentIds.length,
  });
};

/**
 * Track search
 * @param searchTerm - Search query
 */
export const trackFacebookSearch = (searchTerm: string) => {
  trackFacebookEvent('Search', {
    search_string: searchTerm,
  });
};

/**
 * Track lead generation (form submission)
 * @param formType - Type of form (contact, newsletter, etc.)
 */
export const trackFacebookLead = (formType: string = 'contact') => {
  trackFacebookEvent('Lead', {
    content_name: formType,
  });
};

/**
 * Track contact click (phone/email)
 * @param contactType - 'phone' or 'email'
 */
export const trackFacebookContact = (contactType: 'phone' | 'email') => {
  trackFacebookEvent('Contact', {
    contact_type: contactType,
  });
};

/**
 * Track custom "Add to Wishlist" event
 * @param product - Product details
 */
export const trackFacebookAddToWishlist = (product: {
  id?: string;
  title?: string;
  price?: number | string;
}) => {
  trackFacebookEvent('AddToWishlist', {
    content_name: product.title || 'Unknown Product',
    content_ids: [product.id],
    content_type: 'product',
    value: parseFloat(String(product.price)) || 0,
    currency: 'USD',
  });
};
